import React from 'react'

import incomeIcon from 'app/assets/icons/summary_icons/income.svg'
import { Alert, Box } from 'app/components/design-system-components'
import Badge from 'app/components/design-system-components/Badges'
import { Text } from 'app/components/design-system-components/typography'
import ApplicationNoteAdd from 'app/components/display/application_note_add/component'
import NoteButtons from 'app/components/display/application_note_buttons/component'
import * as Display from 'app/components/display/display'
import ExplainerWithHeading from 'app/components/display/smartdocs/explainerWithHeading'
import { normalizeSmartDocsFields } from 'app/components/display/smartdocs/helpers'
import { ContainerHeader } from 'app/components/display/text/standard_text/standard-headings/component'
import { isDocAutoDeleted } from 'app/sm/docs_helpers'
import {
  IncomeAttachments,
  shouldRenderBankStatements,
  urlTo,
} from 'app/sm/helpers'
import { formatBalance } from 'app/sm/proof_of_income/helper'
import { AppDocumentListItemSecondVersion } from 'app/sm/renter_applications/renter_application_detailed/components/app-document-list-item'
import { ApplicationDetailsNumbersComponent } from 'app/sm/renter_applications/renter_application_detailed/components/new_applicant-details'
import { resolveFormCapabilities } from 'app/utils/applications/formCapabilities'
import { trimAndAddEllipsesPastSpecifiedLength } from 'app/utils/strings/helpers'

import 'app/match/apply/application_summary_before_submit/components/income_container/style.scss'

const no = 'No'
const yes = 'Yes'
const notAnswered = 'Not answered'
const redactionStateProcessing = 'processing'
const redactionStateUnavailable = 'unavailable'
const DOC_TYPE_BANK_STATEMENTS = 'BankStatements'

export const getBankStatementRedactionBadge = (doc = {}) => {
  if (doc.redactionState === redactionStateProcessing) {
    return <Badge text="Processing" variant="warning" />
  }
  if (doc.redactionState === redactionStateUnavailable) {
    return <Badge text="Unavailable" variant="error" />
  }

  return null
}

export const shouldDisableBankStatementForRedaction = (doc = {}) =>
  doc.redactionState === redactionStateProcessing ||
  doc.redactionState === redactionStateUnavailable

const IncomeContainer = ({
  componentClassName = '',
  proofOfIncome = {},
  isIncomeEditable = true,
  onEditClick,
  enableNote = false,
  onNewNoteUpdated,
  newNotesContent,
  onNoteIconClicked,
  onNewNoteAdded,
  onNoteCrossClicked,
  interpreteNoteResult = {},
  enableNotesButton,
  displayedApplicant,
  section,
  id = '',
  toggleRequestInfoModal = undefined,
  toggleEditRenterApplication,
  isManagerAdded,
  ballotsEnabled = false,
  toggleDocumentsViewer = undefined,
  smartDocsAnalysis,
  onClickConfirmOKForAnalysis,
  onClickUndoForAnalysis,
  smartDocsForTeamEnabled = false,
  isQLDForm22 = false,
  applicationDetails = {},
  formCapabilities,
}) => {
  const {
    BankStatements = [],
    Payslips = [],
    GovernmentBenefitDocuments = [],
    OtherIncomeDocuments = [],
  } = proofOfIncome

  // Determine if bank statements section should be rendered
  const { config: applicationConfig = {} } = applicationDetails || {}

  const showBankStatements = shouldRenderBankStatements(applicationConfig)
  const resolvedFormCapabilities = resolveFormCapabilities(formCapabilities)
  const showSavingsBalance = resolvedFormCapabilities.hasSavingsBalance

  // Filter income attachments based on config
  const visibleIncomeAttachments = IncomeAttachments.filter((attachment) => {
    if (attachment.docType === DOC_TYPE_BANK_STATEMENTS) {
      return showBankStatements
    }
    return true
  })

  let formedAnalysisData = undefined
  Payslips.forEach((document) => {
    const { guidID } = document || {}
    if (smartDocsAnalysis?.analysis[guidID ?? '']) {
      let smartDocsAnalysisForDocument = []

      if (smartDocsAnalysis?.analysis[guidID]) {
        smartDocsAnalysisForDocument = smartDocsAnalysis?.analysis[guidID]
        const fields = normalizeSmartDocsFields(
          smartDocsAnalysisForDocument?.fields,
        )

        fields.forEach((analysis) => {
          const fileName = trimAndAddEllipsesPastSpecifiedLength(
            document?.name,
            20,
          )
          analysis.doc_id = guidID
          analysis.file_name = fileName
        })

        if (fields.length === 0) {
          return
        }

        smartDocsAnalysisForDocument = {
          ...smartDocsAnalysisForDocument,
          fields,
        }
      }

      formedAnalysisData = {
        ...formedAnalysisData,
        analysis: {
          ...formedAnalysisData?.analysis,
          [guidID]: smartDocsAnalysisForDocument,
        },
      }
    }
  })
  return (
    <div id={id} className={`container-component ${componentClassName}`}>
      <ContainerHeader
        icon={incomeIcon}
        text="Income"
        isEditable={isIncomeEditable}
        onEditClick={() =>
          onEditClick(
            `${urlTo('ProofOfIncome')}?from=rentersummary${
              ballotsEnabled && `&ballot=true`
            }`,
          )
        }
        enableNote={enableNote}
        onNewNoteUpdated={onNewNoteUpdated}
        newNotesContent={newNotesContent}
        onNoteIconClicked={onNoteIconClicked}
        onNewNoteAdded={onNewNoteAdded}
        onNoteCrossClicked={onNoteCrossClicked}
        interpreteNoteResult={interpreteNoteResult}
        enableNotesButton={enableNotesButton}
        displayedApplicant={displayedApplicant}
        section={section}
        toggleRequestInfoModal={toggleRequestInfoModal}
        toggleEditRenterApplication={toggleEditRenterApplication}
        isManagerAdded={isManagerAdded}
      />
      <div>
        <ProofOfIncomComponent
          proofOfIncome={proofOfIncome}
          Payslips={Payslips}
          BankStatements={BankStatements}
          OtherIncomeDocuments={OtherIncomeDocuments}
          GovernmentBenefitDocuments={GovernmentBenefitDocuments}
          displayTitle={false}
          labelClassName="income-label"
          ballotsEnabled={ballotsEnabled}
          toggleDocumentsViewer={toggleDocumentsViewer}
          smartDocsAnalysis={formedAnalysisData}
          onClickConfirmOKForAnalysis={onClickConfirmOKForAnalysis}
          onClickUndoForAnalysis={onClickUndoForAnalysis}
          smartDocsForTeamEnabled={smartDocsForTeamEnabled}
          isQLDForm22={isQLDForm22}
          showBankStatements={showBankStatements}
          visibleIncomeAttachments={visibleIncomeAttachments}
          showSavingsBalance={showSavingsBalance}
        />
      </div>
    </div>
  )
}

const ProofOfIncomComponent = ({
  proofOfIncome,
  displayTitle = true,
  onNewNoteUpdated,
  newNotesContent,
  onNoteIconClicked,
  onNewNoteAdded,
  onNoteCrossClicked,
  displayedApplicant,
  interpreteNoteResult = {},
  enableNotesButton,
  ballotsEnabled = false,
  toggleDocumentsViewer = undefined,
  smartDocsAnalysis = undefined,
  onClickConfirmOKForAnalysis,
  onClickUndoForAnalysis,
  smartDocsForTeamEnabled = false,
  isQLDForm22 = false,
  showBankStatements = true,
  visibleIncomeAttachments = [],
  showSavingsBalance = true,
}) => {
  const {
    optedToHavePropertyInvestor,
    optedToHavePropertyOwnership,
    SavingsBalance = 0,
  } = proofOfIncome
  let propertyInvestor = notAnswered
  if (optedToHavePropertyInvestor === 1) {
    propertyInvestor = no
  } else if (optedToHavePropertyInvestor === 2) {
    propertyInvestor = yes
  }
  let propertyOwner = notAnswered
  if (optedToHavePropertyOwnership === 1) {
    propertyOwner = no
  }
  if (optedToHavePropertyOwnership === 2) {
    propertyOwner = yes
  }

  const savingsBalanceDisplay = formatBalance(SavingsBalance)

  return (
    <div className="application-income-container">
      {displayTitle && (
        <div className="position-relative">
          <h4 className="identity-title pdf-mt0 display-flex justify-content-space-between">
            <span>Income</span>
            {enableNotesButton && (
              <NoteButtons
                onNoteIconClicked={onNoteIconClicked('income')}
                noteStatus={interpreteNoteResult.income}
              />
            )}
          </h4>
          <ApplicationNoteAdd
            onNewNoteUpdated={onNewNoteUpdated}
            onNewNoteAdded={onNewNoteAdded}
            newNotesContent={newNotesContent}
            section="income"
            onNoteCrossClicked={onNoteCrossClicked}
            name={displayedApplicant.firstName}
          />
          <div className="application-seperator" />
        </div>
      )}
      {smartDocsAnalysis && smartDocsForTeamEnabled && (
        <Box my={4}>
          <ExplainerWithHeading
            isCopilot={false}
            data={smartDocsAnalysis}
            onClickConfirmOKForAnalysis={onClickConfirmOKForAnalysis}
            onClickUndoForAnalysis={onClickUndoForAnalysis}
            isRenterSide={false}
            toggleDocumentsViewer={toggleDocumentsViewer}
            showOpenViewerLink={true}
          />
        </Box>
      )}
      {!isQLDForm22 && showBankStatements && showSavingsBalance && (
        <Box mb="5px">
          <Display.SectionMinorTitle text="Savings Balance" />
          <Text as="span">{savingsBalanceDisplay}</Text>
        </Box>
      )}
      <div className="application-identification-container application-income-proof-container width100 flex-direction-row">
        {visibleIncomeAttachments.map((attachment) => {
          const filteredAttachments =
            (proofOfIncome && proofOfIncome[attachment.docType]) || []
          return (
            <div
              className="application-identification-sub-container width-50"
              key={attachment.name}
            >
              <div className="application-info-common-component">
                <div className="title width100">
                  <Display.SectionMinorTitle text={attachment.name} />
                  {filteredAttachments.length > 0 &&
                    filteredAttachments.map((doc) => {
                      const isBankStatement =
                        attachment.docType === DOC_TYPE_BANK_STATEMENTS
                      const disabledForRedaction =
                        isBankStatement &&
                        shouldDisableBankStatementForRedaction(doc)
                      const docDisabled =
                        isDocAutoDeleted(doc) || disabledForRedaction

                      return (
                        <AppDocumentListItemSecondVersion
                          titleClass="mt0"
                          document={doc}
                          key={doc.guidID}
                          docBadge={
                            isBankStatement
                              ? getBankStatementRedactionBadge(doc)
                              : null
                          }
                          toggleDocumentsViewer={
                            toggleDocumentsViewer && !docDisabled
                              ? () => toggleDocumentsViewer(doc.guidID)
                              : undefined
                          }
                          disabled={docDisabled}
                          isAutoDeleted={isDocAutoDeleted(doc)}
                          isSensitive
                        />
                      )
                    })}
                  {filteredAttachments.length === 0 && (
                    <div className="no-info-provided">
                      No information provided
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
        {!ballotsEnabled && (
          <div>
            <div className="application-info-common-component">
              <Alert showLeftIcon={false} variant="blueWithBg" mt={7}>
                <div className="title width100">
                  <Display.SectionMinorTitle
                    text={'Property Investment (optional marketing promotion)'}
                    componentClass="width100"
                  />
                  <ApplicationDetailsNumbersComponent
                    title="Are you a property investor or are you interested in becoming an investor?"
                    text={propertyInvestor}
                    componentClassName="justify-content-unset ha"
                    textClassName="pdf-font-size-10"
                    titleTextClassName="pdf-font-size-10"
                    titleClassName="width100"
                  />
                </div>
              </Alert>
            </div>
          </div>
        )}
        {ballotsEnabled && (
          <div>
            <div className="application-info-common-component">
              <div className="title width100">
                <Display.SectionMinorTitle
                  text={'Property Ownership'}
                  componentClass="width100"
                />
                <ApplicationDetailsNumbersComponent
                  title="Do you own a whole or part of a property in Australia?"
                  text={propertyOwner}
                  componentClassName="justify-content-unset ha"
                  textClassName="pdf-font-size-10"
                  titleTextClassName="pdf-font-size-10"
                  titleClassName="width100"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default IncomeContainer
